<template>
  <div class="campanha-banner" :data-campaign-use-full-filled="campaignUseFullFilled">
    <div class="banner-content">
      <div class="banner-icon-wrap" aria-hidden="true">
        <i class="pi pi-bolt banner-icon"></i>
      </div>

      <div class="banner-text-wrap">
        <p class="banner-kicker" hidden>Campanha ativa{{ campaignLabel }}</p>
        <h3 class="banner-title">{{ campaignTitle }}</h3>

        <p class="banner-description">
          Período promocional ativo{{ periodLabel }}. Você tem até <b>{{ campaignModuleUses }} usos grátis</b>{{ moduleDescriptionSuffix }} neste mês. Aproveite agora!
        </p>

        <div class="flex items-center gap-1 mt-1">
          <div v-if="resolvedModuleNames.length" class="banner-modules">
                <span
              v-for="moduleName in resolvedModuleNames"
                    :key="moduleName"
                    class="banner-module-chip"
                >
                    {{ moduleName }}
                </span>
            </div>
            <CampaingBadgeCounter
                :counter="campaignModuleCounter ?? 0"
                :max="campaignModuleUses"
                mode="normal"
                :module="campaingModuleCode"
            />
        </div>
      </div>

      <button
        v-if="hasPrimaryAction"
        type="button"
        class="banner-action-button"
        @click="handlePrimaryAction"
      >
        {{ primaryLabel }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import CampaingBadgeCounter from '@/components/views/campanhas/CampaingBadgeCounter.vue'
import { useSessionStore } from '@/stores/components/sessionStore'
import type { Campaings } from '@/types/plugins/session'

interface Props {
  campaignId?: string | number | null
  validoDe?: string | null
  validoAte?: string | null
  campaignModuleUses?: number
  campaignModuleCounter?: number
  campaignModuleName?: string | null
  campaignUseFullFilled?: boolean
  campaingModuleCode?: string
  moduleNames?: string[]
  primaryLabel?: string
  onPrimaryAction?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  campaignId: null,
  moduleNames: () => [],
  primaryLabel: '',
  onPrimaryAction: undefined,
  campaingModuleCode: '',
})

const sessionStore = useSessionStore()

function toNumber(value: unknown, fallback = 0): number {
  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : fallback
}

function resolveMaybeRef<T>(maybeRef: unknown): T | null {
  // If Pinia returns a Ref-like object, unwrap `.value` safely.
  if (maybeRef && typeof maybeRef === 'object' && 'value' in (maybeRef as any)) {
    return (maybeRef as any).value as T
  }
  return (maybeRef as T) ?? null
}

const campaignList = computed<Campaings[]>(() => {
  const rawCampaigns = resolveMaybeRef<Campaings[] | Record<string, Campaings> | null>(
    sessionStore.currentCampaings
  )

  if (Array.isArray(rawCampaigns)) {
    return rawCampaigns
  }

  if (rawCampaigns && typeof rawCampaigns === 'object') {
    return Object.values(rawCampaigns)
  }

  return []
})

const currentCampaign = computed<Campaings | null>(() => {
  const campaignId = props.campaignId
  if (campaignId === null || campaignId === undefined || campaignId === '') {
    return null
  }

  const normalizedCampaignId = String(campaignId)

  return (
    campaignList.value.find((campaign) => String(campaign.id) === normalizedCampaignId) ??
    null
  )
})

const currentCampaignModule = computed(() => {
  const campaign = currentCampaign.value
  if (!campaign || !Array.isArray(campaign.modulos) || !campaign.modulos.length) {
    return null
  }

  const moduleCode = String(props.campaingModuleCode || '')
    .trim()
    .toUpperCase()

  if (moduleCode) {
    const moduleByCode = campaign.modulos.find(
      (moduleItem) =>
        String(moduleItem?.codigo || '')
          .trim()
          .toUpperCase() === moduleCode
    )

    if (moduleByCode) {
      return moduleByCode
    }
  }

  return campaign.modulos[0] ?? null
})

const validoDe = computed(() => currentCampaign.value?.valido_de ?? props.validoDe ?? null)
const validoAte = computed(() => currentCampaign.value?.valido_ate ?? props.validoAte ?? null)

const campaignModuleUses = computed(() =>
  toNumber(currentCampaignModule.value?.pivot?.cota_uso, props.campaignModuleUses ?? 0)
)

const campaignModuleCounter = computed(() =>
  toNumber(
    currentCampaignModule.value?.pivot?.cota_usada_total ??
      currentCampaignModule.value?.pivot?.cota_usada,
    props.campaignModuleCounter ?? 0
  )
)

const campaignModuleName = computed(() => {
  const moduleName =
    currentCampaignModule.value?.titulo?.trim() ||
    currentCampaignModule.value?.nome?.trim() ||
    ''

  if (moduleName) {
    return moduleName
  }

  return props.campaignModuleName?.trim() || null
})

const campaignUseFullFilled = computed(() => {
  if (campaignModuleUses.value <= 0) {
    return props.campaignUseFullFilled ?? true
  }

  return campaignModuleCounter.value < campaignModuleUses.value
})

const resolvedModuleNames = computed(() => {
  const moduleNamesFromCampaign = currentCampaign.value?.modulos
    ?.map((moduleItem) => moduleItem.titulo?.trim() || moduleItem.nome?.trim() || '')
    .filter((name): name is string => Boolean(name))

  if (moduleNamesFromCampaign && moduleNamesFromCampaign.length) {
    return moduleNamesFromCampaign
  }

  return props.moduleNames
})

const hasPrimaryAction = computed(() => typeof props.onPrimaryAction === 'function')

const campaignLabel = computed(() =>
  props.campaignId ? ` #${props.campaignId}` : ''
)

const campaignTitle = computed(() => {
  const moduleName = campaignModuleName.value?.trim()
  if (moduleName) {
    return `Campanha especial: ${moduleName}`
  }

  return 'Campanha especial ativa!'
})

const moduleDescriptionSuffix = computed(() => {
  const moduleName = campaignModuleName.value?.trim()
  if (!moduleName) {
    return ''
  }

  return ` no módulo ${moduleName}`
})

const periodLabel = computed(() => {
  const startDate = formatDate(validoDe.value)
  const endDate = formatDate(validoAte.value)

  if (startDate && endDate) {
    return ` (${startDate} a ${endDate})`
  }

  if (endDate) {
    return ` (ate ${endDate})`
  }

  return ''
})

function formatDate(value?: string | null) {
  if (!value) {
    return null
  }

  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) {
    return null
  }

  return parsedDate.toLocaleDateString('pt-BR')
}

function handlePrimaryAction() {
  props.onPrimaryAction?.()
}
</script>

<style scoped lang="scss">
.campanha-banner {
  width: 100%;
  --banner-border: #ebc6a0;
  --banner-bg-from: #fffaf3;
  --banner-bg-mid: #fff0df;
  --banner-bg-to: #fff9f1;
  --banner-accent: #e67f24;
  --banner-accent-soft: #f9e4cd;
  --banner-title: #7f4e1f;
  --banner-text: #8a5e34;
  --banner-kicker: #a06e3d;
  --banner-chip-border: #e6bf96;
  --banner-chip-bg: #fff4e6;
  --banner-button-bg: #dc8b46;
  --banner-button-border: #c86e24;
  --banner-button-hover: #c86e24;
  --banner-glow: #e9944a2e;
  --banner-shadow: #a9652822;
}

.banner-content {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.9rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--banner-border);
  background:
    radial-gradient(circle at 90% 15%, var(--banner-glow) 0, transparent 48%),
    linear-gradient(
      130deg,
      var(--banner-bg-from) 0%,
      var(--banner-bg-mid) 52%,
      var(--banner-bg-to) 100%
    );
  box-shadow:
    inset 0 1px 0 #ffffffd6,
    0 6px 14px var(--banner-shadow);
}

.banner-icon-wrap {
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 0.6rem;
  background-color: var(--banner-accent-soft);
  color: var(--banner-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.banner-icon {
  font-size: 1rem;
}

.banner-text-wrap {
  flex: 1;
  min-width: 0;
}

.banner-kicker {
  margin: 0;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--banner-kicker);
}

.banner-title {
  margin: 0.15rem 0 0;
  font-size: 0.98rem;
  line-height: 1.25;
  font-weight: 700;
  color: var(--banner-title);
}

.banner-description {
  margin: 0.25rem 0 0;
  font-size: 0.86rem;
  line-height: 1.4;
  color: var(--banner-text);
}

.banner-modules {
  margin-top: 0.45rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.banner-module-chip {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--banner-chip-border);
  border-radius: 999px;
  padding: 0.12rem 0.48rem;
  background-color: var(--banner-chip-bg);
  color: var(--banner-text);
  font-size: 0.72rem;
  font-weight: 600;
}

.banner-action-button {
  border: 1px solid var(--banner-button-border);
  border-radius: 0.6rem;
  background-color: var(--banner-button-bg);
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 700;
  padding: 0.48rem 0.8rem;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.banner-action-button:hover {
  background-color: var(--banner-button-hover);
  border-color: #b7621f;
  color: #ffffff;
}

@media (max-width: 920px) {
  .banner-content {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .banner-action-button {
    width: 100%;
  }
}
</style>
